// src/encounter/nurse-med-rec/nurse-med-rec-block.tsx
// [PROD-1653] The nurse's medication reconciliation on the pre-visit: Antonin's MedicineReconciliation card (@scripsteam/signal),
// ported as designed (Claude Design 6c7fbe34, preview/68-case-mariam-first.html), wired to the Scrips.Patient API.
//
// What the card shows is Antonin's; what it saves is the backend's (med-rec-mapping.ts):
//   - every complete answer is saved at once against its prescription order and this visit (POST …/Reconciliation);
//   - the signed-in clinician's name is on every line (`by`); the server stamps the record itself from the login;
//   - a row with no prescription order, or a medicine the patient reports, is shown but cannot be saved yet: one line says so;
//   - the nurse records only. Continue, Change and Stop stay the physician's, in the encounter.
// The card's tokens are scoped to the card (`data-signal-scope`): Antonin's token file would otherwise re-colour the whole app.
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { skipToken, useMutation, useQuery } from '@tanstack/react-query';
import { InlineError, Line, MedicineReconciliation, SkeletonLines } from '@scripsteam/signal';
import '@scripsteam/signal/styles.css';
import signalTokens from '@scripsteam/signal/tokens.css?inline';
import { post } from '@/shared/api/api-client';
import { usePatientMedications } from '@/patients/services/use-patient-medications';
import { changedAnswers, savedTodayLine, toApi, toRecMeds, toReportedMedicationInput, type CardEntry, type ReportedAdded } from './med-rec-mapping';
import { useMedicationSearchTiered } from '@/encounter/services/use-medication-search-tiered';
import { useSaveMedication } from '@/encounter/services/use-save-medication';
import { buildCreateMedicationBody } from '@/encounter/lib/to-medication-request';
import type { MedicationOption } from '@/encounter/lib/to-medication-result';

/** Antonin's catalogue notes ("Changed vs dev…", "From Antonin's Claude Design edit…", `gi-change`, `data-inspector`) are for
 *  his gallery only; his gallery hides them with its own switch (catalog/src/devtools/inspector.css, data-gi-notes), which the
 *  app never loads. Several exported components carry one (Vitals' entry grid on dev, 4 Nov), so they are hidden in every scope. */
export const CATALOGUE_NOTES_HIDDEN = '[data-signal-scope] [data-inspector], [data-signal-scope] .gi-change, [data-signal-scope] .gi-note { display: none; }';
/** Antonin's tokens, applied inside the card only. */
const SCOPED_TOKENS = `${signalTokens.replace(/:root\b/g, '[data-signal-scope]')}\n${CATALOGUE_NOTES_HIDDEN}`;

/** True inside a SignalScope: the tokens are already applied there. */
const InSignalScope = createContext(false);

/** The one place the vendored Signal sheets are loaded (check-no-css allows these two imports in this file only): every Signal card
 *  on the pre-visit (this one, Diagnostics and imaging) and the catalog Intelligence rail renders inside it, with Antonin's tokens
 *  scoped to the card. Inside another scope (the nurse pre-visit template, nurse-previsit-template.tsx) it adds no element: the tokens
 *  are already there, and Antonin's page lays out its cards by their own elements (card 1's timeline joins sibling cards). */
export function SignalScope({ slot, children, className }: { slot: string; children: ReactNode; /** Layout only (the catalog Intelligence rail fills its column). */ className?: string }) {
  const nested = useContext(InSignalScope);
  if (nested) return <>{children}</>;
  return (
    <div data-signal-scope data-slot={slot} className={className}>
      <style>{SCOPED_TOKENS}</style>
      <InSignalScope.Provider value={true}>{children}</InSignalScope.Provider>
    </div>
  );
}

export interface NurseMedRecBlockProps {
  patientId: string | null | undefined;
  encounterId: string | null | undefined;
  /** The signed-in clinician's name, on every recorded line. */
  userName: string | undefined;
  /** The visit's day: only an answer saved on this day is this visit's. */
  visitDay: Date;
  /** The signed-in clinician's provider id: the drug search's favourites and CreateMedication's performer (as the left rail). */
  performerId?: string | null;
}

export function NurseMedRecBlock({ patientId, encounterId, userName, visitDay, performerId }: NurseMedRecBlockProps) {
  const query = usePatientMedications(patientId, true);
  // The left rail adds, edits and stops medicines through saves that refresh the patient summary, not this list: when the summary
  // changes, read the list again, so a medicine added in the rail is on the card without a reload. Never fetches the summary itself.
  const summaryAt = useQuery({ queryKey: ['patient-summary', patientId], queryFn: skipToken }).dataUpdatedAt;
  const refetch = query.refetch;
  const seenSummary = useRef(summaryAt);
  useEffect(() => {
    if (summaryAt !== seenSummary.current) {
      seenSummary.current = summaryAt;
      void refetch();
    }
  }, [summaryAt, refetch]);
  const meds = useMemo(() => toRecMeds(query.data, visitDay), [query.data, visitDay]);
  const orderOf = useMemo(() => new Map(meds.map((m) => [m.name, m.rxOrderId])), [meds]);
  const savedToday = savedTodayLine(meds);
  const last = useRef<Record<string, CardEntry>>({});
  const [failed, setFailed] = useState<string[]>([]);
  const [unsaved, setUnsaved] = useState(false);

  const lastBody = useRef(new Map<string, { rxOrderId: string; body: NonNullable<ReturnType<typeof toApi>> }>());
  const save = useMutation({
    mutationFn: ({ rxOrderId, body }: { name: string; rxOrderId: string; body: NonNullable<ReturnType<typeof toApi>> }) =>
      post<unknown>(`/api/Patients/${patientId}/Medications/${rxOrderId}/Reconciliation`, body),
    onSuccess: (_d, v) => setFailed((f) => f.filter((n) => n !== v.name)),
    onError: (_e, v) => setFailed((f) => (f.includes(v.name) ? f : [...f, v.name])),
  });

  // A medicine the patient reports is a MedicationStatement the app already saves (Samer, 4 Nov): the chart's and the encounter's
  // drug search (coded) and CreateMedication (isOrder false, this visit), exactly as the left rail's "Add medication". No category:
  // the record has none ("an invented field"). Dose and frequency stay in the nurse's words on the card; the list holds the medicine.
  const [medQuery, setMedQuery] = useState('');
  const medSearch = useMedicationSearchTiered(performerId, medQuery);
  const medOptions = useMemo(() => new Map((medSearch.data ?? []).map((o) => [o.id, o])), [medSearch.data]);
  const reportedSearch = useMemo(
    () => ({
      options: (medSearch.data ?? []).map((o) => ({ value: o.id, label: o.name, ...(o.strength ? { detail: o.strength } : {}) })),
      status: medSearch.isError ? ('error' as const) : medSearch.isFetching ? ('loading' as const) : ('ready' as const),
      onQuery: setMedQuery,
      onRetry: () => setMedQuery((q) => `${q}`),
    }),
    [medSearch.data, medSearch.isError, medSearch.isFetching],
  );
  const createMedication = useSaveMedication(patientId ?? undefined);
  // Each added coded medicine, with the search result it was picked from (the search moves on as the nurse types): saved once,
  // and kept until it is, so one added before the visit opens is saved when it opens, and Retry saves a failed one again.
  const seenReported = useRef(new Set<string>());
  // Each is bound to the patient (and visit, once open) it was added on: it is never saved to another one.
  const pendingReported = useRef(new Map<string, { name: string; option: MedicationOption; how: string; patientId: string; encounterId: string | null }>());
  const savingReported = useRef(new Set<string>());
  const [reportedFailed, setReportedFailed] = useState<{ id: string; name: string }[]>([]);

  const saveReported = () => {
    if (!patientId || !encounterId || !performerId) return;
    for (const [id, p] of pendingReported.current) {
      if (p.patientId !== patientId || (p.encounterId && p.encounterId !== encounterId)) {
        pendingReported.current.delete(id);
        setReportedFailed((f) => f.filter((x) => x.id !== id));
        continue;
      }
      if (savingReported.current.has(id)) continue;
      // Bound to this visit at its first save: a failed one is retried here only, never into a later visit.
      p.encounterId = encounterId;
      savingReported.current.add(id);
      createMedication
        .mutateAsync({ kind: 'create', body: buildCreateMedicationBody(toReportedMedicationInput(p.option, p.how), { patientId, encounterId, performerId }) })
        .then(
          () => {
            pendingReported.current.delete(id);
            setReportedFailed((f) => f.filter((x) => x.id !== id));
          },
          () => setReportedFailed((f) => (f.some((x) => x.id === id) ? f : [...f, { id, name: p.name }])),
        )
        .finally(() => savingReported.current.delete(id));
    }
  };
  const saveReportedRef = useRef(saveReported);
  saveReportedRef.current = saveReported;
  useEffect(() => {
    saveReportedRef.current();
  }, [patientId, encounterId, performerId]);

  // Every line on the card is Antonin's (Line, InlineError, SkeletonLines from @scripsteam/signal), inside the scoped tokens: nothing drawn here.
  if (query.isLoading) return <SignalScope slot="med-rec-loading"><SkeletonLines /></SignalScope>;
  if (query.isError) {
    return (
      <SignalScope slot="med-rec-error">
        <InlineError message="The medicines could not be loaded." onRetry={() => void query.refetch()} />
      </SignalScope>
    );
  }

  const retryFailed = () => {
    for (const name of failed) {
      const last = lastBody.current.get(name);
      if (last) save.mutate({ name, ...last });
    }
  };

  /** Queues each newly added coded medicine, as the left rail does, then saves the queue. A free-text one (no coded pick) is not saved. */
  const queueReported = (added: readonly ReportedAdded[]) => {
    for (const a of added) {
      if (!a.medication || seenReported.current.has(a.id) || !patientId) continue;
      const option = medOptions.get(a.medication.value);
      if (!option) continue;
      seenReported.current.add(a.id);
      pendingReported.current.set(a.id, { name: a.name, option, how: a.how, patientId, encounterId: encounterId ?? null });
    }
    saveReported();
  };

  const onChange = (state: { entries: Readonly<Record<string, CardEntry>>; added: readonly ReportedAdded[] }) => {
    queueReported(state.added);
    const entries = state.entries as Record<string, CardEntry>;
    for (const name of changedAnswers(last.current, entries)) {
      const rxOrderId = orderOf.get(name);
      const body = toApi(entries[name], encounterId);
      if (rxOrderId && body) {
        lastBody.current.set(name, { rxOrderId, body });
        save.mutate({ name, rxOrderId, body });
      }
    }
    last.current = entries;
    setUnsaved(Object.keys(entries).some((n) => !orderOf.get(n)));
  };

  return (
    <SignalScope slot="nurse-med-rec">
      {savedToday ? <div role="status" data-slot="med-rec-saved-today"><Line meta>{savedToday}</Line></div> : null}
      <MedicineReconciliation
        key={meds.map((m) => m.name).join('|')}
        meds={meds.map(({ name, dose }) => ({ name, ...(dose ? { dose } : {}) }))}
        role="nurse"
        by={userName}
        onChange={onChange}
        reportedSearch={reportedSearch}
        reportedCategory={false}
      />
      {!encounterId ? <div role="status" data-slot="med-rec-no-visit"><Line meta>Answers are saved once the visit is open.</Line></div> : null}
      {unsaved ? <div role="status" data-slot="med-rec-not-saved"><Line meta>Answers for medicines with no prescription here are not saved yet.</Line></div> : null}
      {reportedFailed.length ? (
        <div data-slot="med-rec-reported-failed">
          <InlineError message={`Not added to the medication list: ${reportedFailed.map((x) => x.name).join(', ')}.`} onRetry={saveReported} />
        </div>
      ) : null}
      {failed.length ? (
        <div data-slot="med-rec-failed">
          <InlineError message={`Not saved: ${failed.join(', ')}.`} onRetry={retryFailed} />
        </div>
      ) : null}
    </SignalScope>
  );
}
